/**
 * JWT utilities
 */

/**
 * Decode a JWT payload without verification
 * Handles both standard base64 and base64url encoding
 */
export function decodeJwtPayload<T = unknown>(jwt: string): T | null {
  try {
    if (!jwt) return null;
    const parts = jwt.split(".");
    if (parts.length < 2) return null;

    // Convert base64url to base64
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");

    // Decode and handle Unicode characters
    const json = atob(payload);
    const decoded = decodeURIComponent(
      json.split("").map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join("")
    );

    return JSON.parse(decoded) as T;
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param jwt The JWT token string
 * @param bufferSec Optional buffer in seconds before actual expiry (default: 60)
 */
export function isTokenExpired(jwt: string, bufferSec = 60): boolean {
  const payload = decodeJwtPayload<{ exp?: number }>(jwt);
  if (!payload?.exp) return true;

  const nowSec = Math.floor(Date.now() / 1000);
  return payload.exp < nowSec + bufferSec;
}

/**
 * Get a specific claim from a JWT token
 */
export function getTokenClaim<T>(jwt: string, claim: string): T | null {
  const payload = decodeJwtPayload<Record<string, unknown>>(jwt);
  if (!payload) return null;
  return (payload[claim] as T) ?? null;
}

/**
 * Get the token_use claim (common in Cognito tokens)
 */
export function getTokenUse(jwt: string): "id" | "access" | null {
  return getTokenClaim<"id" | "access">(jwt, "token_use");
}
